import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileSignature } from "lucide-react";
import api, { apiError } from "@/lib/api";

export const SERVICE_AGREEMENT = "service_agreement";

export function AgreementSign({ slug = SERVICE_AGREEMENT, user, onSigned }) {
    const [a, setA] = useState(null);
    const [name, setName] = useState(user?.name || "");
    const [consent, setConsent] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => { api.get(`/agreements/${slug}`).then((r) => setA(r.data.agreement)).catch(() => setErr("Could not load the agreement.")); }, [slug]);

    const onScroll = (e) => { const t = e.target; if (t.scrollTop + t.clientHeight >= t.scrollHeight - 12) setScrolled(true); };

    const sign = async () => {
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post(`/agreements/${slug}/sign`, { full_name: name, consent, version: a.version });
            onSigned(data.signature);
        } catch (e) { setErr(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };

    if (!a) return <div className="flex justify-center py-10">{err ? <p className="text-sm text-red-500">{err}</p> : <Loader2 className="h-5 w-5 animate-spin text-ocean" />}</div>;
    return (
        <div className="space-y-4" data-testid="agreement-sign">
            <div className="flex items-start gap-3 rounded-xl bg-ocean/5 p-3 text-sm text-navy"><FileSignature className="mt-0.5 h-4 w-4 shrink-0 text-ocean" /><div><b>{a.title}</b> · Version {a.version}<div className="text-xs text-slate-500">Please read to the end, type your full legal name, and confirm consent.</div></div></div>
            <div onScroll={onScroll} data-testid="agreement-body" className="max-h-56 overflow-y-auto whitespace-pre-line rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13px] leading-relaxed text-slate-700">{a.body}</div>
            <div className="space-y-1.5"><Label htmlFor="sig-name">Full legal name (electronic signature)</Label><Input id="sig-name" data-testid="agreement-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan A. Carter" /></div>
            <label className="flex items-start gap-3 text-sm text-slate-700">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="agreement-consent" className="mt-0.5" />
                <span>I have read this agreement and consent to sign it electronically. I understand programs are consultation-only, no fee is collected online, and no outcomes are guaranteed. See <Link to="/legal/privacy" target="_blank" className="font-semibold text-ocean hover:underline">Privacy Notice</Link>.</span>
            </label>
            {!scrolled && <p className="text-xs text-slate-500">Scroll to the end of the agreement to enable signing.</p>}
            {err && <p className="text-sm text-red-500" data-testid="agreement-error">{err}</p>}
            <Button onClick={sign} disabled={busy || !consent || !scrolled || name.trim().length < 2} data-testid="agreement-submit" className="h-11 w-full bg-navy font-semibold text-white hover:bg-navy-light">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign & continue"}
            </Button>
        </div>
    );
}
